// client/src/components/admin/AdminLeagueView.js
import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import PageLayout from '../common/layout/PageLayout'
import useAuth from '../../hooks/useAuth'
import './AdminLeagueView.css'
import PlayoffConfirmationModal from './PlayoffConfirmationModal'

// Extracted components
import LeagueStatusManagementPanel from './league/LeagueStatusManagementPanel'
import DivisionManagementTab from './league-tabs/DivisionManagementTab'
import MatchManagementTab from './league-tabs/MatchManagementTab'
import BracketManagementTab from './league-tabs/BracketManagementTab'
import PendingInvitesTab from './league-tabs/PendingInvitesTab'
import LeagueDeleteModal from './modals/LeagueDeleteModal'
import PlayerRemoveModal from './modals/PlayerRemoveModal'
import LeaguesTable from './league/LeaguesTable'

// Utility functions
import { isPlayoffPhase } from '../../utils/leagueStatusUtils'
import { getDeleteButtonProps } from '../../utils/leagueDeletePermissions'
import { canEditDivisions } from '../../utils/divisionPermissions'
import { getBracketInfo } from '../../utils/bracketCalculations'
import { getActiveLevels } from '../../utils/leagueLevels'

// Custom hooks
import useModalState from '../../hooks/useModalState'
import useLeagueData from '../../hooks/useLeagueData'
import useDivisionAssignments from '../../hooks/useDivisionAssignments'
import useLeagueActions from '../../hooks/useLeagueActions'

export default function AdminLeagueView() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  // UI state
  const [selectedLeagueId, setSelectedLeagueId] = useState('')
  const [activeTab, setActiveTab] = useState('divisions') // 'divisions', 'matches', 'bracket', or 'pending_invites'
  const [isBracketEditMode, setIsBracketEditMode] = useState(false)
  const [selectedBracketLevelId, setSelectedBracketLevelId] = useState(null)

  // Modal state management
  const {
    deleteModal,
    playoffModal,
    removePlayerModal,
    loadingStates,
    openDeleteModal,
    closeDeleteModal,
    openPlayoffModal,
    closePlayoffModal,
    openRemovePlayerModal,
    closeRemovePlayerModal,
    setLoadingState,
  } = useModalState()

  // League data management
  const {
    leagues,
    setLeagues,
    players,
    divisions,
    assignments,
    setAssignments,
    matches,
    bracket,
    setBracket,
    selectedLeague,
    playersById,
    divisionsById,
    loadLeagues,
    loadLeagueData,
    loadBracketData,
  } = useLeagueData(selectedLeagueId, setLoadingState)

  // Division management permissions
  const canEditDivisionsForLeague = useMemo(
    () => canEditDivisions(selectedLeague || {}),
    [selectedLeague],
  )
  const isBracketTabAvailable =
    selectedLeague && isPlayoffPhase(selectedLeague.league_status)
  const isPendingInvitesTabAvailable =
    selectedLeague?.league_type?.toLowerCase() === 'doubles'
  const activeLevels = useMemo(
    () => getActiveLevels(selectedLeague),
    [selectedLeague],
  )
  const hasLevelTabs = activeLevels.length > 1
  const selectedBracketLevel = useMemo(
    () =>
      activeLevels.find((level) => level.id === selectedBracketLevelId) || null,
    [activeLevels, selectedBracketLevelId],
  )

  // Division assignments management
  const {
    newDivision,
    handleDivisionInputChange,
    resetNewDivision,
    hasUnsavedChanges,
    unassignedPlayers,
    handleDragEnd,
  } = useDivisionAssignments({
    players,
    assignments,
    setAssignments,
    selectedLeague,
    canEditDivisionsForLeague,
  })

  // Action handlers
  const {
    handleStatusTransition,
    handleStartPlayoffs,
    handleSkipPlayoffs,
    handlePublishBracket,
    handleLeagueDelete,
    confirmLeagueDelete,
    createDivision,
    deleteDivision,
    updateDivisionName,
    saveAssignments,
    handleRemovePlayerClick,
    confirmRemovePlayer,
    handleDecouplePartner,
    handleDeleteMatch,
    handleSwapHomeAway,
    handleDownloadParticipantsCsv,
    handleBracketUpdate,
  } = useLeagueActions({
    selectedLeagueId,
    selectedLeague,
    leagues,
    setLeagues,
    divisions,
    assignments,
    players,
    unassignedPlayers,
    hasUnsavedChanges,
    loadLeagues,
    loadLeagueData,
    setBracket,
    setIsBracketEditMode,
    openDeleteModal,
    closeDeleteModal,
    openPlayoffModal,
    closePlayoffModal,
    openRemovePlayerModal,
    closeRemovePlayerModal,
    setLoadingState,
    resetNewDivision,
    newDivision,
    setSelectedLeagueId,
  })

  // Wrapper for getDeleteButtonProps that provides user type
  const getDeleteButtonPropsWithUser = (league) => {
    return getDeleteButtonProps(league, user?.user_type)
  }

  // Refetch leagues when navigating to this page or when window regains focus
  // This ensures the list is fresh after creating a league
  useEffect(() => {
    loadLeagues()
  }, [location.pathname, loadLeagues])

  // Auto-load bracket data when switching to bracket tab
  useEffect(() => {
    if (
      activeTab === 'bracket' &&
      selectedLeagueId &&
      selectedLeague &&
      isPlayoffPhase(selectedLeague.league_status) &&
      !(hasLevelTabs && selectedBracketLevelId === null)
    ) {
      loadBracketData(selectedLeagueId, selectedBracketLevelId)
    }
  }, [
    activeTab,
    selectedLeagueId,
    selectedLeague,
    selectedBracketLevelId,
    hasLevelTabs,
    loadBracketData,
  ])

  useEffect(() => {
    if (!selectedLeagueId) {
      setSelectedBracketLevelId(null)
      return
    }

    if (activeLevels.length === 0) {
      setSelectedBracketLevelId(null)
      return
    }

    const hasSelectedActiveLevel = activeLevels.some(
      (level) => level.id === selectedBracketLevelId,
    )

    if (!hasSelectedActiveLevel) {
      setSelectedBracketLevelId(activeLevels[0].id)
    }
  }, [selectedLeagueId, activeLevels, selectedBracketLevelId])

  useEffect(() => {
    if (!selectedLeagueId) {
      setActiveTab('divisions')
      return
    }

    const validTabs = new Set(['divisions', 'matches'])

    if (isBracketTabAvailable) {
      validTabs.add('bracket')
    }

    if (isPendingInvitesTabAvailable) {
      validTabs.add('pending_invites')
    }

    if (!validTabs.has(activeTab)) {
      setActiveTab('divisions')
    }
  }, [
    activeTab,
    selectedLeagueId,
    isBracketTabAvailable,
    isPendingInvitesTabAvailable,
  ])

  return (
    <PageLayout title="League Management" noCard={true}>
      <div className="d-flex justify-content-between align-items-center mb-4 admin-leagues-header">
        <button
          className="btn btn-primary admin-create-league-btn"
          onClick={() => navigate('/admin/leagues/editor')}
          data-track="admin.create_new_league"
        >
          Create New League
        </button>
      </div>

      <LeaguesTable
        leagues={leagues}
        selectedLeagueId={selectedLeagueId}
        isDownloadingCsv={loadingStates.isDownloadingCsv}
        onSelectLeague={setSelectedLeagueId}
        onDownloadCsv={handleDownloadParticipantsCsv}
        onDelete={handleLeagueDelete}
        getDeleteButtonProps={getDeleteButtonPropsWithUser}
      />

      {selectedLeagueId && (
        <>
          {/* Status management panel */}
          {(() => {
            const selectedLeague = leagues.find(
              (l) => l.id === parseInt(selectedLeagueId),
            )
            return (
              selectedLeague && (
                <LeagueStatusManagementPanel
                  league={selectedLeague}
                  onStatusTransition={handleStatusTransition}
                  onSkipPlayoffs={handleSkipPlayoffs}
                />
              )
            )
          })()}

          {/* Tab Navigation */}
          <div className="card mb-4">
            <div className="card-header">
              <ul className="nav nav-tabs card-header-tabs admin-league-tabs">
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === 'divisions' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('divisions')}
                    data-track="admin.tab_divisions"
                  >
                    <i className="fas fa-users me-2"></i>
                    Division Management
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    className={`nav-link ${
                      activeTab === 'matches' ? 'active' : ''
                    }`}
                    onClick={() => setActiveTab('matches')}
                    data-track="admin.tab_matches"
                  >
                    <i className="fas fa-tennis-ball me-2"></i>
                    Match Management
                  </button>
                </li>
                {isBracketTabAvailable && (
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === 'bracket' ? 'active' : ''
                      }`}
                      onClick={() => setActiveTab('bracket')}
                      data-track="admin.tab_bracket"
                    >
                      <i className="fas fa-trophy me-2"></i>
                      Playoff Bracket
                    </button>
                  </li>
                )}
                {isPendingInvitesTabAvailable && (
                  <li className="nav-item">
                    <button
                      className={`nav-link ${
                        activeTab === 'pending_invites' ? 'active' : ''
                      }`}
                      onClick={() => setActiveTab('pending_invites')}
                      data-track="admin.tab_pending_invites"
                    >
                      <i className="fas fa-envelope-open-text me-2"></i>
                      Pending Invites
                    </button>
                  </li>
                )}
              </ul>
            </div>
            <div className="card-body">
              {activeTab === 'divisions' && (
                <DivisionManagementTab
                  league={selectedLeague}
                  newDivision={newDivision}
                  divisions={divisions}
                  players={players}
                  assignments={assignments}
                  onNewDivisionChange={handleDivisionInputChange}
                  onCreateDivision={createDivision}
                  onDragEnd={handleDragEnd}
                  onDeleteDivision={deleteDivision}
                  onUpdateDivisionName={updateDivisionName}
                  onSaveAssignments={saveAssignments}
                  onRemovePlayer={handleRemovePlayerClick}
                  onDecouplePartner={handleDecouplePartner}
                />
              )}

              {activeTab === 'matches' && (
                <MatchManagementTab
                  matches={matches}
                  playersById={playersById}
                  divisionsById={divisionsById}
                  onSwapHomeAway={handleSwapHomeAway}
                  onDeleteMatch={handleDeleteMatch}
                />
              )}

              {activeTab === 'bracket' && (
                <BracketManagementTab
                  league={selectedLeague}
                  leagueId={selectedLeagueId}
                  activeLevels={activeLevels}
                  hasLevelTabs={hasLevelTabs}
                  selectedBracketLevelId={selectedBracketLevelId}
                  selectedBracketLevelSkill={selectedBracketLevel?.skill_level}
                  bracket={bracket}
                  isEditMode={isBracketEditMode}
                  isLoading={loadingStates.isLoadingBracket}
                  isPublishing={loadingStates.isPublishingBracket}
                  onToggleEditMode={setIsBracketEditMode}
                  onBracketLevelChange={setSelectedBracketLevelId}
                  onPublishBracket={handlePublishBracket}
                  onBracketUpdate={handleBracketUpdate}
                  onRefresh={() =>
                    loadBracketData(selectedLeagueId, selectedBracketLevelId)
                  }
                />
              )}

              {activeTab === 'pending_invites' && (
                <PendingInvitesTab league={selectedLeague} />
              )}
            </div>
          </div>
        </>
      )}

      {/* Scary League Deletion Modal */}
      <LeagueDeleteModal
        show={deleteModal.show}
        league={deleteModal.league}
        user={user}
        onClose={closeDeleteModal}
        onConfirm={() => confirmLeagueDelete(deleteModal.league)}
      />

      {/* Playoff Confirmation Modal */}
      <PlayoffConfirmationModal
        isOpen={playoffModal.show}
        onClose={closePlayoffModal}
        onConfirm={() => handleStartPlayoffs(playoffModal.league)}
        onSkipPlayoffs={() => handleSkipPlayoffs(playoffModal.league)}
        leagueName={playoffModal.league?.league_name || ''}
        isSubmitting={loadingStates.isStartingPlayoffs}
        bracketInfo={
          playoffModal.league ? getBracketInfo(players, selectedLeague) : null
        }
        eligibilityInfo={playoffModal.eligibilityInfo}
      />

      {/* Remove Player Confirmation Modal */}
      <PlayerRemoveModal
        show={removePlayerModal.show}
        player={removePlayerModal.player}
        isRemoving={loadingStates.isRemovingPlayer}
        onClose={closeRemovePlayerModal}
        onConfirm={() => confirmRemovePlayer(removePlayerModal.player)}
      />
    </PageLayout>
  )
}
